import {DashboardFilter} from "./dashboard_filter";
import {combineLatest, debounceTime, map, Observable} from "rxjs";
import {RemoteFilterApplicator} from "./dashboard_filter_applicators";
import {DashboardMultiSelectFilter} from "./dashboard_select_filter";
import {Metric} from "../dashboard-config/dashboard-config.model";
import {KPI_FILTER_NAME, METRIC_ID_FILTER_NAME, withLegacyKpiFilterValue} from "../dashboard-config/kpis/kpi-options";
import {INDICATOR_FILTER_NAME, withLegacyIndicatorFilterValue} from "../dashboard-config/kpis/index-indicators";

export class DashboardFilterController {
  filters: DashboardFilter<any>[];
  filterValues$!: Observable<Map<string, any>>;
  filtersEnabled$!: Observable<Map<string, boolean>>;

  constructor(filters: DashboardFilter<any>[]) {
    this.filters = filters;
    this._checkNoFiltersWithSameName();
    this._initializeConnectedFilters();
    this.filterValues$ = this._generateCombinedFilterValuesObservable();
    this.filtersEnabled$ = this._generateCombinedFilterEnabledObservable();
  }

  getFiltersAppliedRemotely(): DashboardFilter<any>[] {
    return this.filters.filter((filter) => filter.applicator instanceof RemoteFilterApplicator && filter.value !== '-1');
  }

  setFilterValue(filterName: string, value: any) {
    this._getFilterByNameElseThrow(filterName).setValue(value);
  }

  setFilterValues(filterValues: Map<string, any>) {
    filterValues.forEach((value, name) => this.setFilterValue(name, value));
  }

  setFilterEnabled(filterName: string, isEnabled: boolean) {
    this._getFilterByNameElseThrow(filterName).isEnabled = isEnabled;
  }

  setFilterValuesIgnoreMissing(filterValues: Map<string, any>, skipNullValues: boolean = true) {
    this._withLegacyKpiFilterValue(filterValues).forEach((value, name) => {
      const filter = this._getFilterByName(name);
      if (filter && !filter.ignoreProductSetting) {
        if (!(skipNullValues && value == null)) {
          filter.setValue(value);
        }
      }
    });
  }

  /** Apply saved slice filter values, including filters that ignore product settings. */
  setSliceFilterValues(filterValues: Map<string, any>, skipNullValues: boolean = true) {
    this._withLegacyKpiFilterValue(filterValues).forEach((value, name) => {
      const filter = this._getFilterByName(name);
      if (filter && !(skipNullValues && value == null)) {
        const coerced =
          filter instanceof DashboardMultiSelectFilter && Array.isArray(value)
            ? value.map((v) => (v == null || typeof v === 'object' ? v : `${v}`))
            : value;
        try {
          filter.setValue(coerced);
        } catch (err) {
          console.warn(`[filter-controller] failed to restore filter "${name}"`, err);
        }
      }
    });
  }

  /**
   * Slices saved before a dashboard switched to the KPI filter store its legacy type
   * filter (`employed: 'total'`, …). Translate it to `kpi` using this dashboard's metric.
   */
  _withLegacyKpiFilterValue(filterValues: Map<string, any>): Map<string, any> {
    const metricIdFilter = this._getFilterByName(METRIC_ID_FILTER_NAME);
    if (!metricIdFilter) {
      return filterValues;
    }
    let values = filterValues;
    if (this._getFilterByName(KPI_FILTER_NAME)) {
      values = withLegacyKpiFilterValue(values, Number(metricIdFilter.value) as Metric);
    }
    if (this._getFilterByName(INDICATOR_FILTER_NAME)) {
      values = withLegacyIndicatorFilterValue(values);
    }
    return values;
  }

  _initializeConnectedFilters() {
    this.filters.forEach((filter) => {
      if (filter.parentFilterSettings) {
        const parentFilter = this._getFilterByNameElseThrow(filter.parentFilterSettings.parentFilterName);
        filter.setParentFilter(parentFilter);
        parentFilter.setChildFilter(filter)
      }
      filter.initialize();
    });
  }

  _generateCombinedFilterValuesObservable() {
    return combineLatest(this.filters.map((filter) => filter.value$)).pipe(
      map((values) => {
        const map = new Map();
        this.filters.forEach((filter, index) => {
          map.set(filter.name, values[index]);
        });
        return map;
      }),
      debounceTime(10),
      // shareReplay(1)
    );
  }

  _generateCombinedFilterEnabledObservable() {
    return combineLatest(this.filters.map((filter) => filter.isEnabled$)).pipe(
        map((values) => {
          const map = new Map();
          this.filters.forEach((filter, index) => {
            map.set(filter.name, values[index]);
          });
          return map;
        }),
        // debounceTime(10),
        // shareReplay(1)
    );
  }

  _getFilterByNameElseThrow(name: string) {
    const filter = this._getFilterByName(name);
    if (!filter) {
      throw new Error(`Filter with name ${name} not found`);
    }
    return filter;
  }

  _getFilterByName(name: string) {
    return this.filters.find((filter) => filter.name === name);
  }

  getFilterByName(name: string): DashboardFilter<any> {
    const filter = this._getFilterByName(name);
    if (!filter) {
      throw new Error(`Filter with name ${name} not found`);
    }
    return filter;
  }

  _checkNoFiltersWithSameName() {
    const names = this.filters.map((filter) => filter.name);
    const uniqueNames = new Set(names);
    if (names.length !== uniqueNames.size) {
      throw new Error('Filters must have unique names');
    }
  }
}
